import { createReducer, on } from '@ngrx/store';

import { DragDropDevices } from '../../shared/directives/drag-drop/drag-drop-helper';

import { InstallationDetailsActions } from './installation-details.actions';
import { DashboardTypes, defaultPanelsOrder } from './installation-details.model';
import { InstallationDetailsState } from './installation-details.state';

export const initInstallationDetailsState: InstallationDetailsState = {
  availablePaidFeatures: [],
  accessList: null,
  installation: {
    value: null,
    error: null,
    loading: false,
  },
  panelsOrder: {
    value: null,
    error: null,
    loaded: false,
  },
  maintenanceChecklist: {
    value: null,
    error: null,
    loaded: false,
    loading: false,
  },
  serviceInstruction: {
    value: [],
    error: null,
    loading: false,
  },
  subscriptionPlan: null,
  subscriptionPlanLoading: false,
  vicarePlans: null,
  monetizationFeatures: {
    value: null,
    error: null,
    loading: false,
  },
  updateBuildingInfoStatus: {
    loading: false,
    error: null,
  },
  isPermamentInspectionFeatureSupported: false,
  loading: false,
  loaded: false,
  error: null,
  deregisterGateway: {
    pending: false,
    success: false,
    error: false,
  },
  selectedDashboardTypeHelper: DashboardTypes.DEFAULT,
  accountsByCompany: {},
};

export const installationDetailsReducer = createReducer(
  initInstallationDetailsState,

  on(
    InstallationDetailsActions.ReadInstallationDetails,
    (): InstallationDetailsState => ({
      ...initInstallationDetailsState,
      installation: {
        value: null,
        error: null,
        loading: true,
      },
    })
  ),

  on(
    InstallationDetailsActions.ReadInstallationDetailsSuccess,
    (state, { payload }): InstallationDetailsState => {
      if (!payload.installation.address.geolocation.timeZone) {
        payload.installation.address.geolocation.timeZone =
          Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'Europe/Berlin';
      }

      return {
        ...state,
        accessList: payload.accessList,
        installation: {
          loading: false,
          error: null,
          value: payload.installation,
        },
        error: null,
        loading: false,
        loaded: true,
      };
    }
  ),

  on(
    InstallationDetailsActions.ReadInstallationDetailsError,
    (state, { error }): InstallationDetailsState => ({
      ...state,
      installation: {
        value: null,
        loading: false,
        error,
      },
      loading: false,
      loaded: true,
      error,
    })
  ),

  on(
    InstallationDetailsActions.selectDashboardTypeHelper,
    (state, { dashboardType }): InstallationDetailsState => ({
      ...state,
      selectedDashboardTypeHelper: dashboardType,
    })
  ),

  on(
    InstallationDetailsActions.getPermamentInspectionFeatureSupportSuccess,
    (state, { isSupported }): InstallationDetailsState => ({
      ...state,
      isPermamentInspectionFeatureSupported: isSupported,
    })
  ),

  on(
    InstallationDetailsActions.LiveUpdateAggregatedStatus,
    (state, { gatewaySerial, aggregatedStatus, lastChangedAt }) => {
      return {
        ...state,
        installation: {
          ...state?.installation,
          value: {
            ...state?.installation?.value,
            gateways: state?.installation?.value?.gateways?.map((gateway) => {
              if (gateway.serial === gatewaySerial) {
                return {
                  ...gateway,
                  aggregatedStatus,
                  lastStatusChangedAt: lastChangedAt,
                };
              }

              return gateway;
            }),
          },
        },
      };
    }
  ),

  on(
    InstallationDetailsActions.SavePanelsOrder,
    (state, { deviceId, panelsOrder }): InstallationDetailsState => {
      const updatedPanels = { ...state.panelsOrder.value, [deviceId]: panelsOrder };

      return {
        ...state,
        panelsOrder: {
          ...state.panelsOrder,
          value: updatedPanels,
        },
      };
    }
  ),

  on(
    InstallationDetailsActions.SavePanelsOrderSuccess,
    (state): InstallationDetailsState => ({
      ...state,
    })
  ),

  on(
    InstallationDetailsActions.GetPanelsOrder,
    (state): InstallationDetailsState => ({
      ...state,
      panelsOrder: {
        value: null,
        error: null,
        loaded: false,
      },
    })
  ),

  on(
    InstallationDetailsActions.GetPanelsOrderSuccess,
    (state, { panelsOrder }): InstallationDetailsState => {
      // add missing panels which are not found in the saved order panel list
      const allPanels = defaultPanelsOrder;
      const mergedPanels = {};

      const fetchedPanelsOrderArray = Object.keys(panelsOrder);
      fetchedPanelsOrderArray.forEach((panelName) => {
        const missingPanels = allPanels.filter(
          (panel) => !panelsOrder[panelName].find((saved) => saved.panel === panel.panel)
        );
        mergedPanels[panelName] = panelsOrder[panelName].concat(missingPanels);
      });

      return {
        ...state,
        panelsOrder: {
          value: mergedPanels,
          loaded: true,
          error: null,
        },
      };
    }
  ),

  on(
    InstallationDetailsActions.GetPanelsOrderError,
    (state, { error }): InstallationDetailsState => {
      const panels = { ...state.panelsOrder.value };
      Object.keys(panels).forEach((panel) => {
        panels[panel] = defaultPanelsOrder;
      });
      panels[DragDropDevices.Dashboard] = defaultPanelsOrder;

      return {
        ...state,
        panelsOrder: {
          value: panels,
          loaded: true,
          error,
        },
      };
    }
  ),

  on(
    InstallationDetailsActions.ReadUserPaidFeatures,
    (state): InstallationDetailsState => ({
      ...state,
      subscriptionPlanLoading: true,
    })
  ),

  on(
    InstallationDetailsActions.ReadUserPaidFeaturesSuccess,
    (state, { deviceWithPaidFeatures }): InstallationDetailsState => ({
      ...state,
      availablePaidFeatures: deviceWithPaidFeatures?.features || [],
      subscriptionPlan: deviceWithPaidFeatures?.plan,
      subscriptionPlanLoading: false,
    })
  ),

  on(
    InstallationDetailsActions.ReadUserPaidFeaturesError,
    (state, { error }): InstallationDetailsState => ({
      ...state,
      error,
      subscriptionPlanLoading: false,
    })
  ),

  on(
    InstallationDetailsActions.GetMaintenanceChecklist,
    (state): InstallationDetailsState => ({
      ...state,
      maintenanceChecklist: {
        ...state.maintenanceChecklist,
        value: null,
        loaded: false,
        loading: true,
        error: null,
      },
    })
  ),

  on(
    InstallationDetailsActions.GetMaintenanceChecklistSuccess,
    (state, { maintenanceChecklist }): InstallationDetailsState => ({
      ...state,
      maintenanceChecklist: {
        ...state.maintenanceChecklist,
        loaded: true,
        loading: false,
        value: maintenanceChecklist,
      },
    })
  ),

  on(
    InstallationDetailsActions.GetMaintenanceChecklistError,
    (state, { error }): InstallationDetailsState => ({
      ...state,
      maintenanceChecklist: {
        ...state.maintenanceChecklist,
        loaded: false,
        loading: false,
        error,
      },
    })
  ),

  on(
    InstallationDetailsActions.GetServiceInstruction,
    (state): InstallationDetailsState => ({
      ...state,
      serviceInstruction: {
        value: [],
        loading: true,
        error: null,
      },
    })
  ),

  on(
    InstallationDetailsActions.GetServiceInstructionSuccess,
    (state, { serviceInstruction }): InstallationDetailsState => ({
      ...state,
      serviceInstruction: {
        value: serviceInstruction,
        loading: false,
        error: null,
      },
    })
  ),

  on(
    InstallationDetailsActions.GetServiceInstructionError,
    (state, { error }): InstallationDetailsState => ({
      ...state,
      serviceInstruction: {
        value: [],
        loading: false,
        error,
      },
    })
  ),

  on(
    InstallationDetailsActions.GetServiceInstructionDefaultLanguage.attempt,
    (state): InstallationDetailsState => ({
      ...state,
      serviceInstruction: {
        value: [],
        loading: true,
        error: null,
      },
    })
  ),
  on(
    InstallationDetailsActions.GetServiceInstructionDefaultLanguage.success,
    (state, { serviceInstruction }): InstallationDetailsState => ({
      ...state,
      serviceInstruction: {
        value: serviceInstruction,
        loading: false,
        error: null,
      },
    })
  ),

  on(
    InstallationDetailsActions.GetServiceInstructionDefaultLanguage.error,
    (state, { error }): InstallationDetailsState => ({
      ...state,
      serviceInstruction: {
        value: [],
        loading: false,
        error,
      },
    })
  ),

  on(
    InstallationDetailsActions.updateBuildingInfo.attempt,
    (state): InstallationDetailsState => ({
      ...state,
      updateBuildingInfoStatus: {
        loading: true,
        error: null,
      },
    })
  ),

  on(
    InstallationDetailsActions.updateBuildingInfo.success,
    (state, { buildingInfo }): InstallationDetailsState => ({
      ...state,
      installation: {
        ...state.installation,
        ...buildingInfo,
      },
      updateBuildingInfoStatus: {
        loading: false,
        error: null,
      },
    })
  ),

  on(
    InstallationDetailsActions.updateBuildingInfo.error,
    (state): InstallationDetailsState => ({
      ...state,
      updateBuildingInfoStatus: {
        loading: false,
        error: null,
      },
    })
  ),

  on(
    InstallationDetailsActions.GetViCarePlans,
    (state): InstallationDetailsState => ({
      ...state,
      vicarePlans: {
        value: null,
        loading: true,
        error: null,
      },
    })
  ),

  on(
    InstallationDetailsActions.GetViCarePlansSuccess,
    (state, { plans }): InstallationDetailsState => ({
      ...state,
      vicarePlans: {
        value: plans,
        loading: false,
        error: null,
      },
    })
  ),

  on(
    InstallationDetailsActions.GetViCarePlansError,
    (state, { error }): InstallationDetailsState => ({
      ...state,
      vicarePlans: {
        value: null,
        loading: false,
        error,
      },
    })
  ),

  on(
    InstallationDetailsActions.GetMonetizationFeatures,
    (state): InstallationDetailsState => ({
      ...state,
      monetizationFeatures: {
        value: null,
        loading: true,
        error: null,
      },
    })
  ),

  on(
    InstallationDetailsActions.GetMonetizationFeaturesSuccess,
    (state, { monetizationFeatures }): InstallationDetailsState => ({
      ...state,
      monetizationFeatures: {
        value: monetizationFeatures,
        loading: false,
        error: null,
      },
    })
  ),

  on(
    InstallationDetailsActions.GetMonetizationFeaturesError,
    (state, { error }): InstallationDetailsState => ({
      ...state,
      monetizationFeatures: {
        value: null,
        loading: false,
        error,
      },
    })
  ),

  on(
    InstallationDetailsActions.deregisterGateway,
    (state): InstallationDetailsState => ({
      ...state,
      deregisterGateway: {
        pending: true,
        success: false,
        error: false,
      },
    })
  ),

  on(
    InstallationDetailsActions.deregisterGatewaySuccess,
    (state): InstallationDetailsState => ({
      ...state,
      deregisterGateway: {
        pending: false,
        success: true,
        error: false,
      },
    })
  ),

  on(
    InstallationDetailsActions.deregisterGatewayError,
    (state): InstallationDetailsState => ({
      ...state,
      deregisterGateway: {
        pending: false,
        success: false,
        error: true,
      },
    })
  ),

  on(
    InstallationDetailsActions.resetDeregisterState,
    (state): InstallationDetailsState => ({
      ...state,
      deregisterGateway: {
        pending: false,
        success: false,
        error: false,
      },
    })
  ),

  on(
    InstallationDetailsActions.getAccountsByCompany.success,
    (state, { accountsByCompany }): InstallationDetailsState => ({
      ...state,
      accountsByCompany,
    })
  ),

  on(
    InstallationDetailsActions.getEmployeesList.attempt,
    (state, { customerNumber }): InstallationDetailsState => {
      const maintainers = state.accessList?.maintainers || [];
      const maintainersWithLoading = maintainers.map((maintainer) => {
        if (maintainer.customerNumber === customerNumber) {
          return {
            ...maintainer,
            loading: true,
            loaded: false,
            error: null,
          };
        }

        return maintainer;
      });

      return {
        ...state,
        accessList: {
          ...state.accessList,
          maintainers: maintainersWithLoading,
        },
      };
    }
  ),

  on(
    InstallationDetailsActions.getEmployeesList.success,
    (state, { employees, customerNumber }): InstallationDetailsState => {
      const maintainers = state.accessList?.maintainers || [];
      const maintainersWithEmployees = maintainers.map((maintainer) => {
        if (maintainer.customerNumber === customerNumber) {
          return {
            ...maintainer,
            employees: employees,
            loaded: true,
            loading: false,
            error: null,
          };
        }

        return maintainer;
      });
      const updatedAccessList = {
        ...state.accessList,
        maintainers: maintainersWithEmployees,
      };

      return {
        ...state,
        accessList: updatedAccessList,
      };
    }
  ),

  on(
    InstallationDetailsActions.getEmployeesList.error,
    (state, { error, customerNumber }): InstallationDetailsState => {
      const maintainers = state.accessList?.maintainers || [];
      const maintainersWithError = maintainers.map((maintainer) => {
        if (maintainer.customerNumber === customerNumber) {
          return {
            ...maintainer,
            loading: false,
            loaded: false,
            error,
          };
        }

        return maintainer;
      });

      return {
        ...state,
        accessList: {
          ...state.accessList,
          maintainers: maintainersWithError,
        },
      };
    }
  )
);
